import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { Client } from "../generated/api-client";

// Origine API (host:port o origine pubblica). Viene sempre aggiunto il prefisso /api
// (allineato al mount FastAPI in main.py).
const rawBase = (
  process.env.REACT_APP_PUBLIC_BASE_URL || "http://localhost:8000"
).replace(/\/$/, "");
export const API_BASE_URL = rawBase.endsWith("/api") ? rawBase : `${rawBase}/api`;

// Il refresh token vive solo in un cookie HttpOnly impostato dal backend: il JS non lo vede mai.
// Con withCredentials il browser lo invia automaticamente alle chiamate /auth/refresh e /auth/logout.
// In localStorage resta solo l'access token.
//
// Pulizia una tantum: le sessioni create prima di questa modifica avevano il refresh token in
// localStorage (leggibile da uno script iniettato via XSS). Rimuoverlo chiude quella esposizione.
localStorage.removeItem("refresh_token");

// Create axios instance with interceptors
export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Endpoint di auth per cui un 401/403 non deve innescare il refresh né il redirect al login
// (credenziali errate, link scaduti, logout...): l'errore va mostrato alla pagina chiamante.
const AUTH_URLS_WITHOUT_REFRESH = [
  "/auth/login",
  "/auth/register",
  "/auth/refresh",
  "/auth/logout",
  "/auth/verify-email",
  "/auth/resend-verification",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/invite",
];

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("access_token");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with refresh token logic
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const url = originalRequest?.url || "";

    // Evita refresh/redirect per le chiamate di auth pubbliche
    if (AUTH_URLS_WITHOUT_REFRESH.some((authUrl) => url.includes(authUrl))) {
      return Promise.reject(error);
    }

    // Se è un errore 401 e non abbiamo già provato a refreshare
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Se stiamo già refreshando, metti in coda
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Il refresh token viaggia nel cookie HttpOnly: nessun body, solo credenziali del browser
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const { access_token } = response.data;
        localStorage.setItem("access_token", access_token);

        // Aggiorna l'header della richiesta originale
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }

        // Processa la coda
        processQueue(null, access_token);
        isRefreshing = false;

        // Riprova la richiesta originale
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Refresh fallito, logout
        processQueue(refreshError, null);
        isRefreshing = false;
        localStorage.removeItem("access_token");
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Create client with the configured axios instance
export const apiClient = new Client(API_BASE_URL, axiosInstance);
